import { useEffect } from "react";
import { Form, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  RootServicePartnerUpdateDto,
  fetchRootServicePartnerById,
  updateRootServicePartner,
} from "@/lib/api/resources/rootServicePartners";
import {
  RootServicepartnerForm,
  RootServicepartnerFormValues,
  addressInitialValues,
  buildAddressPayload,
} from "./components/RootServicepartnerForm";

export const RootServicepartnerEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<RootServicepartnerFormValues>();

  const query = useQuery({
    queryKey: ["root-service-partners", id],
    queryFn: () => {
      if (!id) throw new Error("No service partner ID provided");
      return fetchRootServicePartnerById(id);
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (query.data) {
      form.setFieldsValue({
        name: query.data.name,
        groupName: query.data.groupName,
        email: query.data.email,
        telephone: query.data.telephone,
        website: query.data.website,
        dwproAudaboxNumber: query.data.dwproAudaboxNumber,
        externalReference: query.data.externalReference,
        address: addressInitialValues(query.data, "address"),
        postalAddress: addressInitialValues(query.data, "postalAddress"),
      });
    }
  }, [query.data, form]);

  const mutation = useMutation({
    mutationFn: (values: RootServicepartnerFormValues) => {
      if (!id) throw new Error("No service partner ID provided");
      const payload: RootServicePartnerUpdateDto = {
        name: values.name,
        groupName: values.groupName ?? null,
        email: values.email ?? null,
        telephone: values.telephone ?? null,
        website: values.website ?? null,
        dwproAudaboxNumber: values.dwproAudaboxNumber ?? null,
        externalReference: values.externalReference ?? null,
        address: buildAddressPayload(values.address),
        postalAddress: buildAddressPayload(values.postalAddress),
      };
      return updateRootServicePartner(id, payload);
    },
    onSuccess: () => {
      message.success("ServicePartner opgeslagen");
      queryClient.invalidateQueries({ queryKey: ["root-service-partners"] });
      navigate(`/base/service-partners/${id}`);
    },
    onError: () => {
      message.error("Opslaan mislukt");
    },
  });

  if (!id) return null;

  return (
    <RootServicepartnerForm
      form={form}
      title="ServicePartner bewerken"
      loading={query.isFetching}
      submitting={mutation.isPending}
      submitLabel="Opslaan"
      onSubmit={(values) => mutation.mutate(values)}
      onCancel={() => navigate(`/base/service-partners/${id}`)}
    />
  );
};
